import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { fetchAllPerfumes, formatMoney } from "@/lib/shopify";
import { getProductImageAlt } from "@/lib/image-alt";
import { ResponsiveImage } from "@/components/responsive-image";
import { SIZES } from "@/lib/image-sources";

export function RelatedPerfumes({ currentSlug, limit = 4 }: { currentSlug: string; limit?: number }) {
  const { data: perfumes } = useQuery({
    queryKey: ["shopify-perfumes"],
    queryFn: fetchAllPerfumes,
    staleTime: 5 * 60 * 1000,
  });

  const siblings = (perfumes ?? [])
    .filter((p) => p.handle !== currentSlug && p.handle !== "chapter-vials")
    .slice(0, limit);
  if (siblings.length === 0) return null;

  return (
    <section className="border-t border-border/60 py-20">
      <div className="mx-auto max-w-7xl px-6">
        <div className="flex items-end justify-between gap-6">
          <div>
            <p className="eyebrow">Continue</p>
            <h2 className="mt-3 text-2xl text-parchment sm:text-3xl">
              More from <span className="italic text-gold/90">the house</span>
            </h2>
          </div>
          <Link
            to="/products"
            className="hidden font-display text-xs uppercase tracking-[0.22em] text-gold transition-colors hover:text-gold-soft sm:inline-block"
          >
            All perfumes →
          </Link>
        </div>

        <ul className="mt-10 grid grid-cols-2 gap-x-4 gap-y-8 sm:gap-x-8 sm:gap-y-12 lg:grid-cols-4">
          {siblings.map((p) => {
            const firstVariant = p.variants.edges[0]?.node;
            const price = firstVariant
              ? formatMoney(firstVariant.price.amount, firstVariant.price.currencyCode)
              : null;
            const image = p.featuredImage?.url;
            const imageAlt = getProductImageAlt({
              shopifyAlt: p.featuredImage?.altText,
              handle: p.handle,
              title: p.title,
              subtitle: p.subtitle?.value,
            });
            return (
              <li key={p.id} className="group">
                <Link
                  to="/products/$slug"
                  params={{ slug: p.handle }}
                  className="block overflow-hidden border border-border"
                >
                  <div className="aspect-square overflow-hidden bg-card sm:aspect-[3/4]">
                    {image && (
                      <ResponsiveImage
                        src={image}
                        alt={imageAlt}
                        width={600}
                        height={800}
                        sizes={SIZES.card}
                        pictureClassName="contents"
                        className="h-full w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105"
                      />
                    )}
                  </div>
                </Link>
                <div className="mt-3">
                  <h3 className="font-display text-base tracking-[0.1em] text-parchment">
                    <Link to="/products/$slug" params={{ slug: p.handle }} className="hover:text-gold-soft">
                      {p.title}
                    </Link>
                  </h3>
                  {price && (
                    <p className="mt-1 font-display text-[0.65rem] uppercase tracking-[0.2em] text-muted-foreground">
                      {price}
                    </p>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
